import { z } from 'zod';
import type { PostalCodeToken } from '@sleed/bizcommerce-api';

/**
 * Postal code edit form. Mirrors the current `PostalCodeToken` contract (cd required ≤20,
 * country/region optional, area/city/district ≤100; area/city/district are translated per Accept-Language, BW-8339). The legacy grid's tax-type/status columns are not
 * part of the current DTO and are intentionally omitted. acns/combcd are server metadata.
 */
export const postalCodeSchema = z.object({
	id: z.number().optional(),
	cd: z.string().trim().min(1, 'validation.required').max(20, 'validation.tooLong'),
	cid: z.number().nullable(),
	rid: z.number().nullable(),
	ar: z.string().trim().max(100, 'validation.tooLong').nullable(),
	ct: z.string().trim().max(100, 'validation.tooLong').nullable(),
	dst: z.string().trim().max(100, 'validation.tooLong').nullable(),
	iv: z.boolean(),
	si: z.number({ message: 'validation.required' }).int(),
	acns: z.string().nullable(),
	combcd: z.string().nullable()
});

export type PostalCodeFormValues = z.infer<typeof postalCodeSchema>;

export const EMPTY_POSTAL_CODE: PostalCodeFormValues = {
	cd: '',
	cid: null,
	rid: null,
	ar: null,
	ct: null,
	dst: null,
	iv: true,
	si: 0,
	acns: null,
	combcd: null
};

export function tokenToForm(token: PostalCodeToken): PostalCodeFormValues {
	return {
		id: token.id,
		cd: token.cd ?? '',
		cid: token.cid ?? null,
		rid: token.rid ?? null,
		ar: token.ar ?? null,
		ct: token.ct ?? null,
		dst: token.dst ?? null,
		iv: token.iv ?? false,
		si: token.si ?? 0,
		acns: token.acns ?? null,
		combcd: token.combcd ?? null
	};
}

export function formToToken(form: PostalCodeFormValues): PostalCodeToken {
	return {
		id: form.id,
		cd: form.cd.trim(),
		cid: form.cid,
		rid: form.rid,
		ar: form.ar?.trim() || null,
		ct: form.ct?.trim() || null,
		dst: form.dst?.trim() || null,
		iv: form.iv,
		si: form.si,
		acns: form.acns,
		combcd: form.combcd
	};
}
